/** Artboard size baked into landing-brand-background.html DATA */
export const LANDING_BRAND_BACKGROUND_WIDTH = 1920;
export const LANDING_BRAND_BACKGROUND_HEIGHT = 1080;

export const LANDING_BRAND_SPOTLIGHT_MESSAGE = 'morfi-landing-brand-spotlight' as const;
export const LANDING_BRAND_PLAYBACK_MESSAGE = 'morfi-landing-brand-playback' as const;
export const LANDING_BRAND_READY_MESSAGE = 'morfi-landing-brand-ready' as const;

export type LandingBrandSpotlightPayload = {
  type: typeof LANDING_BRAND_SPOTLIGHT_MESSAGE;
  x: number | null;
  y: number | null;
};

function coverFitArtboard(
  clientX: number,
  clientY: number,
  viewportWidth: number,
  viewportHeight: number,
  viewportLeft: number,
  viewportTop: number,
): { x: number; y: number } {
  const scale = Math.max(
    viewportWidth / LANDING_BRAND_BACKGROUND_WIDTH,
    viewportHeight / LANDING_BRAND_BACKGROUND_HEIGHT,
  );
  const offsetX = viewportLeft + (viewportWidth - LANDING_BRAND_BACKGROUND_WIDTH * scale) / 2;
  const offsetY = viewportTop + (viewportHeight - LANDING_BRAND_BACKGROUND_HEIGHT * scale) / 2;

  return {
    x: (clientX - offsetX) / scale,
    y: (clientY - offsetY) / scale,
  };
}

/** Map viewport client coords to the iframe canvas artboard (fullscreen cover fit). */
export function clientToLandingBrandArtboard(
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  return coverFitArtboard(clientX, clientY, window.innerWidth, window.innerHeight, 0, 0);
}

/** Map client coords using a host rect — matches the iframe's cover fit when embedded. */
export function clientToLandingBrandArtboardInRect(
  clientX: number,
  clientY: number,
  rect: DOMRectReadOnly,
): { x: number; y: number } {
  return coverFitArtboard(clientX, clientY, rect.width, rect.height, rect.left, rect.top);
}

export function buildLandingBrandSpotlightPayload(
  clientX: number,
  clientY: number,
  hostRect?: DOMRectReadOnly,
): LandingBrandSpotlightPayload {
  const { x, y } =
    hostRect === undefined
      ? clientToLandingBrandArtboard(clientX, clientY)
      : clientToLandingBrandArtboardInRect(clientX, clientY, hostRect);
  return { type: LANDING_BRAND_SPOTLIGHT_MESSAGE, x, y };
}

export function buildLandingBrandSpotlightClearPayload(): LandingBrandSpotlightPayload {
  return { type: LANDING_BRAND_SPOTLIGHT_MESSAGE, x: null, y: null };
}

export type LandingBrandPlaybackPayload = {
  type: typeof LANDING_BRAND_PLAYBACK_MESSAGE;
  playing: boolean;
};

export function buildLandingBrandPlaybackPayload(playing: boolean): LandingBrandPlaybackPayload {
  return { type: LANDING_BRAND_PLAYBACK_MESSAGE, playing };
}

export type LandingBrandReadyPayload = {
  type: typeof LANDING_BRAND_READY_MESSAGE;
};

export function buildLandingBrandReadyPayload(): LandingBrandReadyPayload {
  return { type: LANDING_BRAND_READY_MESSAGE };
}

// ALLOW-UNKNOWN: iframe postMessage payloads are untyped at runtime.
export function isLandingBrandReadyPayload(data: unknown): data is LandingBrandReadyPayload {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const payload = data as LandingBrandReadyPayload;
  return payload.type === LANDING_BRAND_READY_MESSAGE;
}

const EMBEDDED_GATE_HALFTONE_SELECTORS = [
  '[data-testid="hero-journey-halftone"]',
  '[data-testid="hero-launch-brand-halftone"]',
] as const;

function isElementInsideGateHalftone(element: Element, halftone: HTMLElement): boolean {
  if (halftone.contains(element)) {
    return true;
  }

  // Same-origin iframes: elementsFromPoint can return nodes from the nested document.
  // They are not descendants of the halftone in the parent tree, but the iframe is.
  if (element.ownerDocument !== document) {
    const frame = element.ownerDocument.defaultView?.frameElement;
    return frame instanceof Element && halftone.contains(frame);
  }

  return false;
}

/**
 * Embedded hero gate: enable spotlight over the decorative canvas without
 * stealing clicks from mission/vision copy. The halftone subtree stays
 * pointer-events-none; when the cursor is inside the halftone bounds and only
 * non-interactive layers are under the point, treat it as gate hover.
 */
function findEmbeddedGateHalftoneAt(clientX: number, clientY: number): HTMLElement | null {
  for (const selector of EMBEDDED_GATE_HALFTONE_SELECTORS) {
    const halftone = document.querySelector(selector);
    if (!(halftone instanceof HTMLElement)) {
      continue;
    }

    const rect = halftone.getBoundingClientRect();
    const insideHalftone =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;
    if (insideHalftone) {
      return halftone;
    }
  }

  return null;
}

function isLandingHeaderElement(element: Element): boolean {
  return element.closest('header') !== null;
}

export function isEmbeddedGateSpotlightActive(clientX: number, clientY: number): boolean {
  const halftone = findEmbeddedGateHalftoneAt(clientX, clientY);
  if (halftone === null) {
    return false;
  }

  const stack = document.elementsFromPoint(clientX, clientY);
  for (const element of stack) {
    if (!(element instanceof Element)) {
      continue;
    }
    if (window.getComputedStyle(element).pointerEvents === 'none') {
      continue;
    }
    if (isElementInsideGateHalftone(element, halftone)) {
      return true;
    }
    // Decorative band wrappers (full-bleed host, main, etc.) sit above the
    // pointer-events-none halftone subtree but should not block spotlight.
    if (element.contains(halftone)) {
      continue;
    }
    // Fixed landing header is transparent over the hero band — keep spotlight live.
    if (isLandingHeaderElement(element)) {
      continue;
    }
    return false;
  }

  return true;
}
